
import Vue from 'vue';

import { BlockCollection } from '@headlines/exports';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import { GetUniversalHeadlines } from '@root/modules/headlines/types/headlines';
import { categories } from '@root/modules/customer/config/customer.config';
import { GetCustomerData, CustomerData } from '@root/modules/customer/types/customer';
import { pageContentService } from '@root/modules/feed/services/pageContent.service';
import { Headline } from '@headlines/types/headline';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';

interface Category {
  key: string;
  name: string;
  categories: number[];
}

interface Data {
  categories: Category[];
  usersHeadlines: Record<string, Headline[]>;
  topHeadlines: Headline[];
  recommendedHeadlines: Headline[];
  readingHistoryHeadlines: Headline[];
  bookmarksHeadlines: Headline[];
  headlineSettings: Record<string, any>;
  pager: Record<string, any>;
  excludedCategories: number[];
}

interface Props {
  usersCategories: GetCustomerData['data']['data'] | null;
  readingHistoryHeadlinesIds: number[];
  bookmarksHeadlinesIds: number[];
}
interface Methods {
  getCategoryName: (key: string) => string;
  getCategoryIds: (key: string) => number[];
  getHeadlines: (key: string) => void;
  getTopHeadlines: () => void;
  getReadingHistoryHeadlines: () => void;
  getBookmarksHeadlines: () => void;
  getArticleHeadlines: (headlineIds: number[], limit: number) => any;
}

export default Vue.extend<Data, Methods, unknown, Props>({
  components: {
    AdFormBanner,
    BlockType3: BlockCollection.BlockType3,
    BlockType8: BlockCollection.BlockType8,
    BlockType104: BlockCollection.BlockType104,
    BlockType114: BlockCollection.BlockType114,
  },
  props: {
    usersCategories: {
      type: Object as () => Props['usersCategories'],
      default: null,
    },
    readingHistoryHeadlinesIds: {
      type: Array as () => number[],
      required: true,
    },
    bookmarksHeadlinesIds: {
      type: Array as () => number[],
      required: true,
    },
  },
  data() {
    return {
      categories,
      usersHeadlines: {},
      topHeadlines: [],
      recommendedHeadlines: [],
      readingHistoryHeadlines: [],
      bookmarksHeadlines: [],
      headlineSettings: { type: 'grid', icons: true, category: true, author: true, bookmark: true, lead: false, spaceBetweenPerBreakpoint: [16, 20, 24] },
      pager: {
        limit: 15,
        offset: 0,
        count: 0,
      },
      excludedCategories: [69292510, 69292512], // non lithuanian content
    };
  },
  methods: {
    getCategoryName(key) {
      const item = this.categories.find((item) => item.key === key);
      const categoryName = item?.name ? item?.name : '';
      return categoryName;
    },
    getCategoryIds(key) {
      const item = this.categories.find((item) => item.key === key);
      const categoryIds = item?.categories ? item?.categories : [];
      return categoryIds;
    },
    async getHeadlines(key) {
      const categoryIds = this.getCategoryIds(key);

      if (!categoryIds?.length) {
        return;
      }

      const universalHeadlinesService = new UniversalHeadlinesService();

      const headlines = await universalHeadlinesService.fetch({
        categoryId: categoryIds,
        excludeCategories: [300, 69292510],
        attributes: [
          {
            key: ['frontPageSponsoredContent'],
            value: ['false'],
          },
        ],
        offset: this.pager.offset,
        limit: this.pager.limit,
        getCount: true,
      });

      let headlinesItems = headlines.items as Headline[];

      const firstPayedIndex = headlinesItems.findIndex((item) => item.content.paywall?.enabled);

      if (firstPayedIndex > 0) {
        const [firstPaiedItem] = headlinesItems.splice(firstPayedIndex, 1);
        headlinesItems.unshift(firstPaiedItem);
      }

      for (const headline of headlinesItems) {
        headline.href = 'https://delfi.lt/a/' + headline.id;
      }

      if (headlinesItems) {
        headlinesItems = headlinesItems.slice(0, 7);
      }

      if (headlinesItems) {
        this.usersHeadlines[key] = headlinesItems;
      }
    },
    async getTopHeadlines() {
      const baseVariables = {
        attributes: [
          {
            key: ['frontPageSponsoredContent'],
            value: ['false'],
          },
        ],
        orderBy: 'VIEWS',
        orderOptions: { viewsSince: 'SINCE_3_HOURS' },
        offset: this.pager.offset,
        limit: this.pager.limit,
        getCount: true,
      } as GetUniversalHeadlines['variables'];

      let allSelectedCategoryIds: number[] = [];

      if (this.usersCategories?.items) {
        this.usersCategories?.items.forEach((category: any) => {
          const categoryIds = this.getCategoryIds(category.value);
          if (categoryIds) {
            allSelectedCategoryIds = [...allSelectedCategoryIds, ...categoryIds];
          }
        });
      }

      if (!allSelectedCategoryIds?.length) {
        return;
      }

      const universalHeadlinesService = new UniversalHeadlinesService();

      const markedHeadlineCategory = this.usersCategories?.items.some((obj) => obj.value === 'tinklalaides')
        ? this.categories.find((obj) => obj.key === 'tinklalaides')
        : this.categories.find((obj) => obj.key === 'video');

      const firstHeadlineVariables = {
        ...baseVariables,
        categoryId: markedHeadlineCategory?.categories,
        limit: 1,
      };

      const firstHeadline = await universalHeadlinesService.fetch(firstHeadlineVariables);
      const excludeCategories = markedHeadlineCategory?.categories.concat(this.excludedCategories);

      const selectedHeadlinesVariables = {
        ...baseVariables,
        categoryId: allSelectedCategoryIds,
        excludeCategories,
      };

      const headlines = await universalHeadlinesService.fetch(selectedHeadlinesVariables);

      const selectedHeadlines = headlines.items as Headline[];
      if (selectedHeadlines) {
        const firstPayedIndex = selectedHeadlines.findIndex((item) => item.content.paywall?.enabled);

        if (firstPayedIndex > 0) {
          const [firstPaiedItem] = selectedHeadlines.splice(firstPayedIndex, 1);
          if (firstPaiedItem) {
            selectedHeadlines.unshift(firstPaiedItem);
          }
        }
      }

      const headlinesItems = [...firstHeadline.items, ...headlines.items] as unknown as Headline[];

      for (const headline of headlinesItems) {
        headline.href = 'https://delfi.lt/a/' + headline.id;
      }
      if (headlinesItems) {
        this.topHeadlines = headlinesItems.slice(0, 3);
      }
    },
    async getReadingHistoryHeadlines() {
      const headlinesIds = this.readingHistoryHeadlinesIds as number[];
      const headlines = await this.getArticleHeadlines(headlinesIds, 12);
      this.readingHistoryHeadlines = headlines;
    },
    async getBookmarksHeadlines() {
      const headlines = await this.getArticleHeadlines(this.bookmarksHeadlinesIds, 12);
      this.bookmarksHeadlines = headlines;
    },
    async getArticleHeadlines(headlineIds, limit = 0) {
      const universalHeadlinesService = new UniversalHeadlinesService();
      const headlines = await universalHeadlinesService.fetch({
        id: headlineIds,
        attributes: [
          {
            key: ['frontPageSponsoredContent'],
            value: ['false'],
          },
        ],
        limit,
      });

      const headlinesItems = headlines.items as Headline[];

      for (const headline of headlinesItems) {
        headline.href = 'https://delfi.lt/a/' + headline.id;
      }
      if (headlinesItems) {
        return headlinesItems;
      }
    },
  },
  async fetch() {
    if (this.usersCategories?.items?.length) {
      await Promise.all(
        this.usersCategories.items.map(async (item: CustomerData) => {
          await this.getHeadlines(item.value);
        })
      );
    }

    try {
      const lang = 'lt';
      const domain = 'www.delfi.lt/plius';
      const data = await pageContentService.fetch({ domain, language: lang.toUpperCase() });
      if (data) {
        const neededSectionIndex = 0;
        const neededBlockIndex = 1;
        const items = data.feed[neededSectionIndex]?.blocks[neededBlockIndex]?.data?.items as Headline[];

        for (const headline of items) {
          headline.href = 'https://delfi.lt/a/' + headline.id;
        }

        this.recommendedHeadlines = items;
      }
    } catch (e) {
      console.log(e);
    }
  },
  mounted() {
    this.getTopHeadlines();
    this.getReadingHistoryHeadlines();
    this.getBookmarksHeadlines();
  },
});
